import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Package, Plus, Loader2, Sparkles, TrendingUp, Target, BarChart3,
  RefreshCw, Trash2, Award, ArrowUpRight, Megaphone, ShoppingCart,
  Zap, Users, DollarSign, Flame, Star, FlaskConical, Play,
  CheckCircle, XCircle, FileText, Crown, Layers,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const SUB_TABS = [
  { key: "ideas", label: "Sản phẩm", icon: Package },
  { key: "high", label: "Ưu tiên cao", icon: Crown },
  { key: "category", label: "Theo danh mục", icon: Layers },
  { key: "revenue", label: "Doanh thu", icon: DollarSign },
  { key: "test", label: "Test Mode", icon: FlaskConical },
  { key: "trends", label: "Xu hướng", icon: Flame },
];

const PRIORITY_C = {
  HIGH: { label: "Cao", color: "bg-red-500/20 text-red-400" },
  MEDIUM: { label: "TB", color: "bg-amber-500/20 text-amber-400" },
  LOW: { label: "Thấp", color: "bg-slate-500/20 text-slate-400" },
};

const STATUS_C = {
  idea: { label: "Ý tưởng", color: "bg-slate-500/20 text-slate-400" },
  testing: { label: "Đang test", color: "bg-amber-500/20 text-amber-400" },
  approved: { label: "Đã duyệt", color: "bg-lime-500/20 text-lime-400" },
  rejected: { label: "Từ chối", color: "bg-red-500/20 text-red-400" },
};

const CAT_LABELS = {
  kids_food_drinks: "Đồ ăn & nước trẻ em",
  sports_nutrition: "Dinh dưỡng thể thao",
  sports_fashion: "Thời trang thể thao",
  cafe_beverages: "Cafe & đồ uống",
  breakfast_family: "Bữa sáng & gia đình",
  tournament_products: "Sản phẩm giải đấu",
  gym_accessories: "Phụ kiện gym",
};

const CHART_COLORS = ["#84cc16", "#06b6d4", "#f59e0b", "#ef4444", "#a855f7", "#ec4899", "#14b8a6"];

function formatPrice(n) {
  if (!n) return "0d";
  return n.toLocaleString("vi-VN") + "d";
}

export default function ProductLabPanel() {
  const [subTab, setSubTab] = useState("ideas");
  return (
    <div className="flex-1 overflow-hidden flex flex-col" data-testid="product-lab-panel">
      <div className="flex gap-1 px-4 py-2 border-b border-[#1e293b] bg-[#0a0f1a] overflow-x-auto">
        {SUB_TABS.map(t => (
          <button key={t.key} onClick={() => setSubTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium whitespace-nowrap transition-colors ${subTab === t.key ? "bg-[#1e293b] text-cyan-400" : "text-slate-500 hover:text-slate-300"}`}
            data-testid={`plab-tab-${t.key}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto">
        {subTab === "ideas" && <IdeasTab />}
        {subTab === "high" && <HighPriorityTab />}
        {subTab === "category" && <CategoryTab />}
        {subTab === "revenue" && <RevenueTab />}
        {subTab === "test" && <TestModeTab />}
        {subTab === "trends" && <TrendsTab />}
      </div>
    </div>
  );
}

// ==================== IDEAS TAB ====================
function IdeasTab() {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [genCategory, setGenCategory] = useState("");
  const [detail, setDetail] = useState(null);

  const fetchIdeas = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/product-lab/ideas");
      setIdeas(data.ideas || []);
    } catch { toast.error("Lỗi tải data"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchIdeas(); }, [fetchIdeas]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/product-lab/generate?category=${genCategory}`);
      toast.success(`AI da tao ${data.count} san pham!`);
      fetchIdeas();
    } catch { toast.error("Lỗi AI"); }
    finally { setGenerating(false); }
  };

  const deleteIdea = async (id) => {
    try { await api.delete(`/product-lab/ideas/${id}`); fetchIdeas(); toast.success("Đã xoá"); } catch {}
  };

  const doAction = async (id, type) => {
    try {
      const { data } = await api.post(`/product-lab/ideas/${id}/action?action_type=${type}`);
      toast.success(data.message);
      fetchIdeas();
    } catch { toast.error("Lỗi"); }
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="ideas-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><Package className="w-5 h-5 text-cyan-400" /> AI Product Lab</h2>
        <div className="flex gap-2 items-center">
          <select value={genCategory} onChange={e => setGenCategory(e.target.value)} className="bg-[#1e293b] border border-[#334155] rounded-md px-2 py-1.5 text-xs text-slate-300">
            <option value="">Tất cả danh mục</option>
            {Object.entries(CAT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <Button onClick={generate} disabled={generating} className="bg-cyan-500 hover:bg-cyan-400 text-[#0f172a] gap-1.5 text-xs" data-testid="generate-ideas-btn">
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            AI tạo sản phẩm
          </Button>
        </div>
      </div>

      {loading ? <Loader2 className="w-6 h-6 text-cyan-400 animate-spin mx-auto mt-8" /> :
      ideas.length === 0 ? (
        <div className="text-center py-12">
          <Package className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-sm text-slate-500">Chưa có ý tưởng. Bam "AI tạo sản phẩm" để bắt đầu.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {ideas.map(idea => (
            <IdeaCard key={idea.id} idea={idea} onDelete={deleteIdea} onAction={doAction} onDetail={setDetail} />
          ))}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="bg-[#1e293b] border-[#334155] text-slate-200 max-w-lg max-h-[85vh] overflow-y-auto">
          {detail && <IdeaDetail idea={detail} onAction={doAction} onClose={() => setDetail(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function IdeaCard({ idea, onDelete, onAction, onDetail }) {
  return (
    <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4 hover:border-[#475569]/50 transition-colors" data-testid={`idea-${idea.id}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 cursor-pointer" onClick={() => onDetail(idea)}>
          <div className="flex items-center gap-2 mb-0.5">
            <h3 className="text-sm font-medium text-slate-200">{idea.product_name}</h3>
            <Badge className={`text-[8px] border-0 ${PRIORITY_C[idea.priority]?.color || ""}`}>{PRIORITY_C[idea.priority]?.label || idea.priority}</Badge>
            {idea.is_trending && <Badge className="text-[8px] border-0 bg-orange-500/20 text-orange-400">Trending</Badge>}
            {idea.is_bundle && <Badge className="text-[8px] border-0 bg-purple-500/20 text-purple-400">Bundle</Badge>}
            {idea.is_seasonal && <Badge className="text-[8px] border-0 bg-teal-500/20 text-teal-400">Seasonal</Badge>}
          </div>
          <p className="text-xs text-slate-400">{idea.description}</p>
        </div>
        <div className="flex flex-col items-end shrink-0 ml-3">
          <span className={`text-sm font-bold ${idea.total_score >= 70 ? "text-cyan-400" : idea.total_score >= 45 ? "text-amber-400" : "text-slate-400"}`}>{idea.total_score}</span>
          <span className="text-[8px] text-slate-600">diem</span>
        </div>
      </div>

      <div className="flex items-center gap-3 text-[10px] text-slate-500 mb-2">
        <span>Gia: <span className="text-slate-300">{formatPrice(idea.selling_price)}</span></span>
        <span>Margin: <span className="text-lime-400">{idea.margin_percentage}%</span></span>
        <span>DT/tháng: <span className="text-cyan-400">{formatPrice(idea.monthly_revenue)}</span></span>
        <span className="text-slate-600">{CAT_LABELS[idea.category] || idea.category}</span>
      </div>

      <div className="flex gap-1.5 flex-wrap">
        <button onClick={() => onAction(idea.id, "pos")} className="text-[9px] text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded hover:bg-cyan-500/20">Tạo POS</button>
        <button onClick={() => onAction(idea.id, "campaign")} className="text-[9px] text-purple-400 bg-purple-500/10 px-2 py-0.5 rounded hover:bg-purple-500/20">Campaign</button>
        <button onClick={() => onAction(idea.id, "content")} className="text-[9px] text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded hover:bg-amber-500/20">Content</button>
        <button onClick={() => onAction(idea.id, "menu")} className="text-[9px] text-lime-400 bg-lime-500/10 px-2 py-0.5 rounded hover:bg-lime-500/20">Menu</button>
        <button onClick={() => onDelete(idea.id)} className="text-[9px] text-red-400 bg-red-500/10 px-2 py-0.5 rounded ml-auto hover:bg-red-500/20">Xoá</button>
      </div>
    </div>
  );
}

function IdeaDetail({ idea, onAction, onClose }) {
  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-cyan-400 flex items-center gap-2"><Package className="w-5 h-5" /> {idea.product_name}</DialogTitle>
      </DialogHeader>
      <div className="space-y-3 mt-2">
        <p className="text-xs text-slate-400">{idea.description}</p>

        <div className="grid grid-cols-2 gap-2">
          <div className="bg-[#0f172a] rounded-lg p-2"><p className="text-[9px] text-slate-600 uppercase">KH muc tieu</p><p className="text-[11px] text-slate-300 mt-0.5">{idea.target_customer}</p></div>
          <div className="bg-[#0f172a] rounded-lg p-2"><p className="text-[9px] text-slate-600 uppercase">Use case</p><p className="text-[11px] text-slate-300 mt-0.5">{idea.use_case}</p></div>
          <div className="bg-[#0f172a] rounded-lg p-2"><p className="text-[9px] text-slate-600 uppercase">Phu hop</p><p className="text-[11px] text-slate-300 mt-0.5">{idea.fit_with_karinox}</p></div>
          <div className="bg-[#0f172a] rounded-lg p-2"><p className="text-[9px] text-slate-600 uppercase">Do kho</p><p className="text-[11px] text-slate-300 mt-0.5">{idea.ease_of_implementation}</p></div>
        </div>

        <div className="bg-[#0f172a] rounded-lg p-3">
          <p className="text-[9px] text-cyan-400 uppercase mb-2">Pricing & Revenue</p>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div><p className="text-[9px] text-slate-600">Giá bán</p><p className="text-xs font-bold text-slate-200">{formatPrice(idea.selling_price)}</p></div>
            <div><p className="text-[9px] text-slate-600">Giá vốn</p><p className="text-xs font-bold text-slate-200">{formatPrice(idea.estimated_cost)}</p></div>
            <div><p className="text-[9px] text-slate-600">Lợi nhuận</p><p className="text-xs font-bold text-lime-400">{formatPrice(idea.profit_per_unit)}</p></div>
            <div><p className="text-[9px] text-slate-600">Margin</p><p className="text-xs font-bold text-cyan-400">{idea.margin_percentage}%</p></div>
            <div><p className="text-[9px] text-slate-600">Bán/ngày</p><p className="text-xs font-bold text-slate-200">{idea.daily_sales}</p></div>
            <div><p className="text-[9px] text-slate-600">DT/tháng</p><p className="text-xs font-bold text-emerald-400">{formatPrice(idea.monthly_revenue)}</p></div>
          </div>
        </div>

        <div className="bg-[#0f172a] rounded-lg p-3">
          <p className="text-[9px] text-amber-400 uppercase mb-2">Go-To-Market</p>
          <div className="space-y-1.5">
            {idea.marketing_idea && <p className="text-[10px] text-slate-300"><span className="text-slate-500">Marketing:</span> {idea.marketing_idea}</p>}
            {idea.content_idea && <p className="text-[10px] text-slate-300"><span className="text-slate-500">Content:</span> {idea.content_idea}</p>}
            {idea.promotion_plan && <p className="text-[10px] text-slate-300"><span className="text-slate-500">Khuyến mãi:</span> {idea.promotion_plan}</p>}
            {idea.upsell_strategy && <p className="text-[10px] text-slate-300"><span className="text-slate-500">Upsell:</span> {idea.upsell_strategy}</p>}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Badge className={`text-[9px] border-0 ${PRIORITY_C[idea.priority]?.color || ""}`}>{idea.priority}</Badge>
          <span className="text-[10px] text-slate-500">ROI: {idea.roi_score} | Ease: {idea.ease_score} | Impact: {idea.impact_score}</span>
        </div>

        <div className="flex gap-1.5 pt-2 border-t border-[#334155]">
          <Button onClick={() => { onAction(idea.id, "pos"); onClose(); }} size="sm" className="bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500/20 border border-cyan-500/20 h-7 text-[10px] gap-1"><ShoppingCart className="w-3 h-3" /> Tạo POS</Button>
          <Button onClick={() => { onAction(idea.id, "campaign"); onClose(); }} size="sm" className="bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 border border-purple-500/20 h-7 text-[10px] gap-1"><Megaphone className="w-3 h-3" /> Campaign</Button>
          <Button onClick={() => { onAction(idea.id, "content"); onClose(); }} size="sm" className="bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/20 h-7 text-[10px] gap-1"><FileText className="w-3 h-3" /> Content</Button>
        </div>
      </div>
    </>
  );
}

// ==================== HIGH PRIORITY ====================
function HighPriorityTab() {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/product-lab/ideas?priority=HIGH&sort_by=total_score").then(r => { setIdeas(r.data.ideas || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const doAction = async (id, type) => {
    try { const { data } = await api.post(`/product-lab/ideas/${id}/action?action_type=${type}`); toast.success(data.message); } catch { toast.error("Lỗi"); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>;

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="high-priority-tab">
      <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><Crown className="w-5 h-5 text-red-400" /> Sản phẩm ưu tiên cao</h2>
      {ideas.length === 0 ? <p className="text-slate-500 text-center py-8">Chưa có sản phẩm HIGH priority. Tạo ý tưởng trước.</p> :
      <div className="space-y-2">
        {ideas.map((idea, i) => (
          <div key={idea.id} className="bg-red-500/5 border border-red-500/20 rounded-xl p-4" data-testid={`high-${idea.id}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-red-400 font-bold">#{i + 1}</span>
                  <h3 className="text-sm font-medium text-slate-200">{idea.product_name}</h3>
                  <span className="text-sm font-bold text-cyan-400">{idea.total_score}</span>
                </div>
                <p className="text-xs text-slate-400 mt-0.5">{idea.description}</p>
              </div>
            </div>
            <div className="flex items-center gap-4 text-[10px] text-slate-500 mb-2">
              <span>Margin: <span className="text-lime-400 font-bold">{idea.margin_percentage}%</span></span>
              <span>DT/tháng: <span className="text-cyan-400 font-bold">{formatPrice(idea.monthly_revenue)}</span></span>
              <span>Lợi nhuận: <span className="text-emerald-400 font-bold">{formatPrice(idea.monthly_profit)}</span></span>
              <span className="text-slate-600">{CAT_LABELS[idea.category]}</span>
            </div>
            <div className="flex gap-1.5">
              <button onClick={() => doAction(idea.id, "pos")} className="text-[9px] text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded">Tạo POS</button>
              <button onClick={() => doAction(idea.id, "campaign")} className="text-[9px] text-purple-400 bg-purple-500/10 px-2 py-0.5 rounded">Campaign</button>
              <button onClick={() => doAction(idea.id, "content")} className="text-[9px] text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded">Content</button>
            </div>
          </div>
        ))}
      </div>}
    </div>
  );
}

// ==================== CATEGORY TAB ====================
function CategoryTab() {
  const [selected, setSelected] = useState("");
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);

  useEffect(() => {
    api.get("/product-lab/dashboard").then(r => setDashboard(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selected) { setIdeas([]); return; }
    setLoading(true);
    api.get(`/product-lab/ideas?category=${selected}`).then(r => { setIdeas(r.data.ideas || []); setLoading(false); }).catch(() => setLoading(false));
  }, [selected]);

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="category-tab">
      <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><Layers className="w-5 h-5 text-cyan-400" /> Theo danh muc</h2>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {Object.entries(CAT_LABELS).map(([k, v]) => (
          <button key={k} onClick={() => setSelected(k)}
            className={`p-3 rounded-xl border text-left transition-colors ${selected === k ? "bg-cyan-500/10 border-cyan-500/30 text-cyan-400" : "bg-[#1e293b] border-[#334155] text-slate-400 hover:border-[#475569]"}`}
            data-testid={`cat-${k}`}>
            <p className="text-xs font-medium">{v}</p>
            <p className="text-lg font-bold mt-1">{dashboard?.category_counts?.[k] || 0}</p>
          </button>
        ))}
      </div>

      {selected && (
        loading ? <Loader2 className="w-5 h-5 text-cyan-400 animate-spin mx-auto mt-4" /> :
        ideas.length === 0 ? <p className="text-slate-500 text-center py-4 text-sm">Chưa có sản phẩm trong danh mục này.</p> :
        <div className="space-y-2">
          {ideas.map(idea => (
            <div key={idea.id} className="bg-[#1e293b] border border-[#334155] rounded-xl p-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm text-slate-200">{idea.product_name}</h3>
                    <Badge className={`text-[8px] border-0 ${PRIORITY_C[idea.priority]?.color}`}>{idea.priority}</Badge>
                  </div>
                  <p className="text-[10px] text-slate-500 mt-0.5">{idea.description}</p>
                </div>
                <div className="text-right shrink-0 ml-3">
                  <p className="text-xs font-bold text-cyan-400">{formatPrice(idea.monthly_revenue)}</p>
                  <p className="text-[9px] text-slate-500">margin {idea.margin_percentage}%</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== REVENUE TAB ====================
function RevenueTab() {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/product-lab/ideas?sort_by=monthly_revenue").then(r => { setIdeas(r.data.ideas || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>;

  const totalPotential = ideas.reduce((s, i) => s + (i.monthly_revenue || 0), 0);
  const totalProfit = ideas.reduce((s, i) => s + (i.monthly_profit || 0), 0);

  const chartData = ideas.slice(0, 8).map(i => ({
    name: i.product_name.length > 15 ? i.product_name.slice(0, 15) + "..." : i.product_name,
    revenue: i.monthly_revenue,
    profit: i.monthly_profit,
  }));

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="revenue-tab">
      <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><DollarSign className="w-5 h-5 text-emerald-400" /> Tiềm năng doanh thu</h2>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-3 text-center">
          <p className="text-[9px] text-slate-500 uppercase">Tổng DT tiềm năng/thang</p>
          <p className="text-xl font-bold text-emerald-400">{(totalPotential / 1000000).toFixed(1)}M</p>
        </div>
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-3 text-center">
          <p className="text-[9px] text-slate-500 uppercase">Tổng lợi nhuận/thang</p>
          <p className="text-xl font-bold text-lime-400">{(totalProfit / 1000000).toFixed(1)}M</p>
        </div>
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-3 text-center">
          <p className="text-[9px] text-slate-500 uppercase">Số sản phẩm</p>
          <p className="text-xl font-bold text-cyan-400">{ideas.length}</p>
        </div>
      </div>

      {chartData.length > 0 && (
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
          <p className="text-xs text-slate-400 mb-3">Top sản phẩm theo doanh thu</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} layout="vertical">
              <XAxis type="number" tick={{ fill: "#64748b", fontSize: 9 }} />
              <YAxis dataKey="name" type="category" tick={{ fill: "#94a3b8", fontSize: 9 }} width={120} />
              <Tooltip contentStyle={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 8, fontSize: 11, color: "#e2e8f0" }} formatter={(v) => formatPrice(v)} />
              <Bar dataKey="revenue" name="Doanh thu" fill="#06b6d4" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="space-y-1.5">
        {ideas.map((idea, i) => (
          <div key={idea.id} className="flex items-center justify-between bg-[#1e293b] border border-[#334155] rounded-lg px-4 py-2">
            <div className="flex items-center gap-2">
              <span className="text-xs text-cyan-400 font-bold w-5">#{i + 1}</span>
              <span className="text-xs text-slate-200">{idea.product_name}</span>
              <Badge className={`text-[8px] border-0 ${PRIORITY_C[idea.priority]?.color}`}>{idea.priority}</Badge>
            </div>
            <div className="flex items-center gap-3 text-[10px]">
              <span className="text-emerald-400 font-bold">{formatPrice(idea.monthly_revenue)}</span>
              <span className="text-lime-400">{idea.margin_percentage}%</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ==================== TEST MODE ====================
function TestModeTab() {
  const [tests, setTests] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resultDialog, setResultDialog] = useState(null);
  const [resultForm, setResultForm] = useState({ units_sold: 0, revenue: 0, cost: 0, customer_feedback: "", period_days: 7 });

  useEffect(() => {
    Promise.all([
      api.get("/product-lab/test-mode").then(r => setTests(r.data.tests || [])),
      api.get("/product-lab/ideas?status=idea").then(r => setIdeas(r.data.ideas || [])),
    ]).finally(() => setLoading(false));
  }, []);

  const startTest = async (ideaId) => {
    try {
      const { data } = await api.post(`/product-lab/test-mode/${ideaId}/start?batch_size=10&test_days=7`);
      toast.success(data.message);
      // Refresh
      const r1 = await api.get("/product-lab/test-mode");
      setTests(r1.data.tests || []);
    } catch { toast.error("Lỗi"); }
  };

  const submitResult = async () => {
    if (!resultDialog) return;
    try {
      const { data } = await api.post(`/product-lab/test-mode/${resultDialog}/result`, resultForm);
      toast.success(data.message);
      setResultDialog(null);
      const r1 = await api.get("/product-lab/test-mode");
      setTests(r1.data.tests || []);
    } catch { toast.error("Lỗi"); }
  };

  const decide = async (ideaId) => {
    try {
      const { data } = await api.post(`/product-lab/test-mode/${ideaId}/decide`);
      toast.success(`${data.decision}: ${data.reason}`);
      const r1 = await api.get("/product-lab/test-mode");
      setTests(r1.data.tests || []);
    } catch { toast.error("Lỗi"); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>;

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="test-mode-tab">
      <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><FlaskConical className="w-5 h-5 text-amber-400" /> Test Product Mode</h2>
      <p className="text-xs text-slate-500">Tạo batch nhỏ, đo lường bán hàng, AI quyết định Scale hay Stop.</p>

      {/* Running tests */}
      {tests.filter(t => t.status === "running").length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-amber-400 font-medium">Đang test</p>
          {tests.filter(t => t.status === "running").map(t => (
            <div key={t.id} className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-slate-200">{t.product_name}</h3>
                <Badge className="text-[8px] border-0 bg-amber-500/20 text-amber-400">Đang test</Badge>
              </div>
              <p className="text-[10px] text-slate-500 mb-2">Batch: {t.batch_size} | {t.test_days} ngay | KQ: {t.results?.length || 0} lan nhap</p>
              <div className="flex gap-1.5">
                <Button onClick={() => setResultDialog(t.idea_id)} size="sm" className="bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 h-6 text-[9px] gap-1"><Plus className="w-3 h-3" /> Nhập KQ</Button>
                <Button onClick={() => decide(t.idea_id)} size="sm" className="bg-lime-500/10 text-lime-400 border border-lime-500/20 h-6 text-[9px] gap-1"><Sparkles className="w-3 h-3" /> AI Quyết định</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Completed tests */}
      {tests.filter(t => t.status === "completed").length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-slate-400 font-medium">Đã hoàn thành</p>
          {tests.filter(t => t.status === "completed").map(t => (
            <div key={t.id} className={`rounded-xl p-3 border ${t.decision === "SCALE" ? "bg-lime-500/5 border-lime-500/20" : t.decision === "STOP" ? "bg-red-500/5 border-red-500/20" : "bg-[#1e293b] border-[#334155]"}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-200">{t.product_name}</span>
                <Badge className={`text-[8px] border-0 ${t.decision === "SCALE" ? "bg-lime-500/20 text-lime-400" : t.decision === "STOP" ? "bg-red-500/20 text-red-400" : "bg-amber-500/20 text-amber-400"}`}>{t.decision}</Badge>
              </div>
              {t.reason && <p className="text-[10px] text-slate-400 mt-1">{t.reason}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Start new test */}
      {ideas.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-slate-400 font-medium">Sản phẩm có thể test</p>
          {ideas.slice(0, 5).map(idea => (
            <div key={idea.id} className="flex items-center justify-between bg-[#1e293b] border border-[#334155] rounded-lg px-3 py-2">
              <div>
                <span className="text-xs text-slate-200">{idea.product_name}</span>
                <span className="text-[10px] text-slate-500 ml-2">({formatPrice(idea.selling_price)})</span>
              </div>
              <Button onClick={() => startTest(idea.id)} size="sm" className="bg-amber-500/10 text-amber-400 border border-amber-500/20 h-6 text-[9px] gap-1"><Play className="w-3 h-3" /> Test</Button>
            </div>
          ))}
        </div>
      )}

      {/* Result dialog */}
      <Dialog open={!!resultDialog} onOpenChange={() => setResultDialog(null)}>
        <DialogContent className="bg-[#1e293b] border-[#334155] text-slate-200 max-w-sm">
          <DialogHeader><DialogTitle className="text-amber-400">Nhập kết quả test</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div><Label className="text-slate-400 text-[10px]">Số lượng bán</Label><Input type="number" value={resultForm.units_sold} onChange={e => setResultForm({...resultForm, units_sold: +e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-0.5 h-8 text-xs" /></div>
            <div><Label className="text-slate-400 text-[10px]">Doanh thu (VNĐ)</Label><Input type="number" value={resultForm.revenue} onChange={e => setResultForm({...resultForm, revenue: +e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-0.5 h-8 text-xs" /></div>
            <div><Label className="text-slate-400 text-[10px]">Chi phí (VNĐ)</Label><Input type="number" value={resultForm.cost} onChange={e => setResultForm({...resultForm, cost: +e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-0.5 h-8 text-xs" /></div>
            <div><Label className="text-slate-400 text-[10px]">Feedback KH</Label><Input value={resultForm.customer_feedback} onChange={e => setResultForm({...resultForm, customer_feedback: e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-0.5 h-8 text-xs" placeholder="Khách hàng nói gì?" /></div>
            <div><Label className="text-slate-400 text-[10px]">Số ngày test</Label><Input type="number" value={resultForm.period_days} onChange={e => setResultForm({...resultForm, period_days: +e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-0.5 h-8 text-xs" /></div>
            <Button onClick={submitResult} className="w-full bg-amber-500 hover:bg-amber-400 text-[#0f172a] font-semibold h-9 text-xs" data-testid="submit-test-result-btn">Lưu kết quả</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== TRENDS ====================
function TrendsTab() {
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchTrends = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/product-lab/trends");
      setTrends(data.trends || []);
    } catch { toast.error("Lỗi"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTrends(); }, []);

  const TYPE_C = {
    trending: { label: "Trending", color: "bg-orange-500/20 text-orange-400", icon: Flame },
    bundle: { label: "Bundle", color: "bg-purple-500/20 text-purple-400", icon: Layers },
    seasonal: { label: "Seasonal", color: "bg-teal-500/20 text-teal-400", icon: Star },
    high_margin: { label: "High Margin", color: "bg-lime-500/20 text-lime-400", icon: TrendingUp },
    limited: { label: "Limited", color: "bg-red-500/20 text-red-400", icon: Zap },
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="trends-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><Flame className="w-5 h-5 text-orange-400" /> Xu huong & Goi y</h2>
        <Button onClick={fetchTrends} disabled={loading} variant="outline" size="sm" className="border-[#334155] text-slate-300 hover:text-cyan-400 gap-1.5 text-xs" data-testid="refresh-trends-btn">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> {loading ? "Dang phan tich..." : "Lam moi"}
        </Button>
      </div>

      {loading && trends.length === 0 ? <Loader2 className="w-6 h-6 text-cyan-400 animate-spin mx-auto mt-8" /> :
      trends.length === 0 ? <p className="text-slate-500 text-center py-8">Bấm "Làm mới" để AI phân tích xu hướng.</p> :
      <div className="space-y-2">
        {trends.map((t, i) => {
          const tc = TYPE_C[t.type] || TYPE_C.trending;
          return (
            <div key={i} className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
              <div className="flex items-start justify-between mb-1">
                <div className="flex items-center gap-2">
                  <Badge className={`text-[8px] border-0 ${tc.color}`}>{tc.label}</Badge>
                  <h3 className="text-sm font-medium text-slate-200">{t.product_name}</h3>
                </div>
                {t.estimated_margin && <span className="text-xs text-lime-400 font-bold">{t.estimated_margin}%</span>}
              </div>
              <p className="text-xs text-slate-400">{t.description}</p>
              {t.why_trending && <p className="text-[10px] text-orange-400/80 mt-1">{t.why_trending}</p>}
              <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-500">
                {t.selling_price && <span>Gia: <span className="text-slate-300">{formatPrice(t.selling_price)}</span></span>}
              </div>
            </div>
          );
        })}
      </div>}
    </div>
  );
}
